// Svelte-free by design, so a `node:test` file can exercise it.

/**
 * Gli stati in cui una fattura emessa ha una copia di cortesia scaricabile.
 *
 * Copia speculare di COURTESY_PDF_STATUSES in src/fatturapa/pdf/eligibility.js:
 * il frontend non può importare codice del backend. La copia è disponibile
 * dall'accettazione del trasporto SdI; dopo uno scarto la fattura si considera
 * mai emessa. test/frontend-courtesy-pdf.test.js tiene allineate le due liste.
 */
export const COURTESY_PDF_STATUSES = Object.freeze([
  'accepted_by_transport',
  'delivered',
  'notifica_mancata_consegna',
  'decorrenza_termini',
  'esito_cedente_accettato',
  'esito_cedente_rifiutato',
  'completed',
  'imported',
]);

/**
 * Vero quando la copia di cortesia si può scaricare: fattura nostra accettata
 * dal trasporto SdI, o in un successivo stato emesso. Il backend rifiuta con 409 il resto;
 * qui serve a spegnere i pulsanti invece di insegnare un errore.
 *
 * @param {{ direction?: string, status?: string } | null | undefined} invoice
 * @returns {boolean}
 */
export function canDownloadCourtesyPdf(invoice) {
  return invoice?.direction === 'outbound'
    && COURTESY_PDF_STATUSES.includes(invoice.status);
}

/**
 * The note shown beside the courtesy-PDF controls for states without the PDF,
 * plus the pending-final-outcome note at transport acceptance.
 *
 * @param {{ direction?: string, status?: string } | null | undefined} invoice
 * @returns {{ messageKey: string, hintKey: string|null } | null}
 */
export function courtesyPdfStatusCopy(invoice) {
  if (invoice?.direction !== 'outbound') return null;
  if (invoice.status === 'accepted_by_transport') {
    return {
      messageKey: 'invoices.files.pdfAcceptedNote',
      hintKey: 'invoices.files.pdfAcceptedHint',
    };
  }
  if (invoice.status === 'scarto') {
    return {
      messageKey: 'invoices.files.pdfUnavailableScarto',
      hintKey: 'invoices.files.pdfUnavailableHint',
    };
  }
  if (!canDownloadCourtesyPdf(invoice)) {
    return {
      messageKey: 'invoices.files.pdfUnavailable',
      hintKey: 'invoices.files.pdfUnavailableHint',
    };
  }
  return null;
}

/**
 * I valori dell'intestazione `X-Courtesy-Xml` di GET …/pdf: se il PDF contiene
 * al suo interno il file XML archiviato. Copia speculare dell'enum nella
 * specifica OpenAPI (test/frontend-courtesy-pdf.test.js le tiene allineate).
 */
export const COURTESY_XML_STATES = Object.freeze(['embedded', 'omitted', 'unavailable']);

/**
 * Lo stato dell'XML dentro il PDF scaricato, letto dall'intestazione.
 *
 * @param {string|null|undefined} value - `X-Courtesy-Xml` così come arriva
 * @returns {'embedded'|'omitted'|'unavailable'|null} null quando manca o non
 *   si capisce (un server più vecchio): in quel caso non si dice nulla
 */
export function courtesyXmlState(value) {
  const state = String(value ?? '').trim().toLowerCase();
  return COURTESY_XML_STATES.includes(state) ? state : null;
}

/**
 * La chiave dell'avviso da mostrare dopo il download, o null quando non c'è
 * niente da dire: il testo della pagina promette l'XML "di norma", e quando
 * questa copia non lo contiene va detto subito, non scoperto dal cliente.
 *
 * @param {string|null} state - da courtesyXmlState()
 * @returns {string|null}
 */
export function courtesyXmlNoticeKey(state) {
  if (state === 'unavailable') return 'invoices.files.pdfXmlUnavailable';
  if (state === 'omitted') return 'invoices.files.pdfXmlOmitted';
  return null;
}
